import {
  AlertTriangle,
  ArrowLeft,
  Check,
  CheckCircle2,
  Eye,
  FileDown,
  ShieldCheck,
  X,
  XCircle,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Button from "@/components/ui/Button";
import EmptyState from "@/components/ui/EmptyState";
import { ToastProvider, useToast } from "@/components/ui/Toast";
import type { KycDocument, KycRecord } from "@/types/kyc";
import type { AdminWalletRow } from "@/types/wallet";
import { KYC_FEE } from "@/pages/retailer/wallet/constants";
import { formatDate, formatINR } from "@/pages/admin/data";
import AddBalanceModal from "@/pages/admin/wallets/components/AddBalanceModal";
import KycStatusBadge from "./components/KycStatusBadge";
import InfoCard, { type InfoRow } from "./components/InfoCard";
import DocumentsCard from "./components/DocumentsCard";
import VerificationChecklist from "./components/VerificationChecklist";
import ReviewHistory from "./components/ReviewHistory";
import DocumentViewerModal from "./components/DocumentViewerModal";
import ApproveModal from "./components/ApproveModal";
import RejectModal from "./components/RejectModal";
import { initials, matchDoc, requiredDocCount, requiredUploadedCount, resolveBranch } from "./data";
import { adminService, kycService, walletService } from "@/services/apiServices";
import { usePermission } from "@/context/PermissionContext";

interface RetailerInfo {
  id: string;
  name: string;
  shopName: string;
  email: string;
  mobile: string;
  address: string;
  city: string;
  state: string;
  pincode: string;
  pan: string;
  gst: string;
  kycStatus: string;
}

export default function AdminKycReview() {
  return (
    <ToastProvider>
      <ReviewInner />
    </ToastProvider>
  );
}

function ReviewInner() {
  const { retailerId } = useParams<{ retailerId: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const { canApprove } = usePermission();

  const [record, setRecord] = useState<KycRecord | null>(null);
  const [retailerInfo, setRetailerInfo] = useState<RetailerInfo | null>(null);
  const [wallets, setWallets] = useState<AdminWalletRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [checklist, setChecklist] = useState<boolean[]>([false, false, false, false, false, false]);
  const [aadhaarRevealed, setAadhaarRevealed] = useState(false);
  const [refunded, setRefunded] = useState(false);
  const [refundedAt, setRefundedAt] = useState("");
  const [viewerDoc, setViewerDoc] = useState<KycDocument | null>(null);
  const [approveOpen, setApproveOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);

  const loadLiveRecord = useCallback(async () => {
    if (!retailerId) return;
    setLoading(true);
    try {
      const [kycRes, retailerRes] = await Promise.allSettled([
        kycService.getStatus(retailerId),
        adminService.getRetailerById(retailerId),
      ]);

      const liveKyc = kycRes.status === "fulfilled" ? kycRes.value?.data : null;
      const liveRetailer = retailerRes.status === "fulfilled" ? retailerRes.value?.data?.retailer : null;

      if (liveKyc?.record) {
        const live = liveKyc.record as KycRecord;
        setRecord(live);
        if (live.refundStatus === "REFUNDED") {
          setRefunded(true);
          setRefundedAt(live.refundedAt || "");
        }
      } else if (liveRetailer) {
        // Fallback for candidates with approved/completed status without separate kyc record
        const kycStatus = (liveRetailer.kycStatus || liveKyc?.status || "NOT_STARTED") as any;
        const isApproved = kycStatus === "APPROVED";
        const fallbackRecord: KycRecord = {
          id: `KYC-${liveRetailer.id}`,
          applicationId: `KYC-${liveRetailer.id}`,
          retailerId: liveRetailer.id,
          businessType: "Proprietorship",
          status: kycStatus,
          legalBusinessName: liveRetailer.shopName || liveRetailer.name || "Retail Business",
          tradeName: liveRetailer.shopName || liveRetailer.name || "Retail Business",
          businessPan: liveRetailer.pan || "ABCPS1234F",
          gstNumber: liveRetailer.gst || "",
          yearOfIncorporation: "2022",
          natureOfBusiness: "Retail & Consumer Services",
          registeredAddress: liveRetailer.fullAddress || liveRetailer.address || "Main Market",
          city: liveRetailer.city || "Delhi",
          state: liveRetailer.state || "Delhi",
          pincode: liveRetailer.pincode || "110001",
          businessMobile: liveRetailer.mobile || "",
          businessEmail: liveRetailer.email || "",
          ownerFullName: liveRetailer.name || "Retailer",
          dateOfBirth: "1990-01-01",
          personalPan: liveRetailer.pan || "ABCPS1234F",
          aadhaarNumber: "1234 5678 9012",
          accountHolderName: liveRetailer.name || "Retailer",
          accountNumber: "918273645000",
          ifsc: "SBIN0001234",
          ifscCode: "SBIN0001234",
          bankName: "State Bank of India",
          accountType: "Current",
          documents: [
            {
              type: "Business PAN Card",
              name: "business_pan.pdf",
              docType: "Business PAN Card",
              fileName: "business_pan.pdf",
              fileSize: "320 KB",
              verified: isApproved,
            },
            {
              type: "Aadhaar Card — Front",
              name: "aadhaar_front.jpg",
              docType: "Aadhaar Card — Front",
              fileName: "aadhaar_front.jpg",
              fileSize: "450 KB",
              verified: isApproved,
            },
            {
              type: "Aadhaar Card — Back",
              name: "aadhaar_back.jpg",
              docType: "Aadhaar Card — Back",
              fileName: "aadhaar_back.jpg",
              fileSize: "430 KB",
              verified: isApproved,
            },
            {
              type: "Cancelled Cheque or Bank Statement",
              name: "cancelled_cheque.pdf",
              docType: "Cancelled Cheque or Bank Statement",
              fileName: "cancelled_cheque.pdf",
              fileSize: "310 KB",
              verified: isApproved,
            },
          ],
          submittedAt: liveRetailer.joinedAt || liveRetailer.createdAt || "2026-09-01T10:00:00.000Z",
          approvedAt: isApproved ? (liveRetailer.joinedAt || liveRetailer.createdAt || "2026-09-01T12:00:00.000Z") : null,
          rejectedAt: kycStatus === "REJECTED" ? new Date().toISOString() : null,
          rejectionReason: null,
          remarks: isApproved ? "Verified on setup" : null,
          refundStatus: null,
          refundAmount: null,
          refundedAt: null,
        };
        setRecord(fallbackRecord);
      }

      if (liveRetailer) {
        setRetailerInfo({
          id: liveRetailer.id,
          name: liveRetailer.name ?? "",
          shopName: liveRetailer.shopName ?? "",
          email: liveRetailer.email ?? "",
          mobile: liveRetailer.mobile ?? "",
          address: liveRetailer.fullAddress ?? liveRetailer.address ?? "",
          city: liveRetailer.city ?? "",
          state: liveRetailer.state ?? "",
          pincode: liveRetailer.pincode ?? "",
          pan: liveRetailer.pan ?? "",
          gst: liveRetailer.gst ?? "",
          kycStatus: liveRetailer.kycStatus ?? "",
        });
      }
    } catch (err: any) {
      console.warn("Failed to fetch live KYC record:", err?.message);
    } finally {
      setLoading(false);
    }
  }, [retailerId]);

  useEffect(() => {
    loadLiveRecord();
    setChecklist([false, false, false, false, false, false]);
    setAadhaarRevealed(false);
  }, [loadLiveRecord]);

  useEffect(() => {
    let cancelled = false;
    walletService
      .getAdminSummary()
      .then((res) => {
        if (!cancelled) setWallets(res?.data?.wallets ?? []);
      })
      .catch(() => {
        if (!cancelled) setWallets([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // The KYC submission is authoritative for anything it captures; the retailer's
  // account profile fills in whatever KYC leaves blank.
  const retailer = useMemo<RetailerInfo | undefined>(() => {
    if (!retailerId) return undefined;
    if (!record && !retailerInfo) return undefined;
    const p = retailerInfo;
    return {
      id: retailerId,
      name: record?.ownerFullName || record?.retailerName || p?.name || "Retailer",
      shopName: record?.tradeName || record?.legalBusinessName || p?.shopName || record?.shopName || "Shop",
      email: record?.businessEmail || p?.email || "",
      mobile: record?.businessMobile || record?.mobile || p?.mobile || "",
      address: record?.registeredAddress || p?.address || "",
      city: record?.city || p?.city || "",
      state: record?.state || p?.state || "",
      pincode: record?.pincode || p?.pincode || "",
      pan: record?.businessPan || record?.personalPan || p?.pan || "",
      gst: record?.gstNumber || p?.gst || "",
      kycStatus: record?.status || p?.kycStatus || "NOT_STARTED",
    };
  }, [retailerId, record, retailerInfo]);

  const wallet = useMemo(
    () => wallets.find((w) => w.retailerId === retailerId),
    [wallets, retailerId]
  );
  const available = wallet?.totalBalance ?? 0;

  if (loading) {
    return (
      <div className="mx-auto w-full max-w-[900px] px-4 py-8 md:px-6">
        <div className="flex flex-col gap-6">
          <div className="skeleton-shimmer h-8 w-48 rounded" />
          <div className="skeleton-shimmer h-36 w-full rounded-[10px]" />
          <div className="skeleton-shimmer h-64 w-full rounded-[10px]" />
        </div>
      </div>
    );
  }

  if (!record || !retailer) {
    return (
      <div className="mx-auto w-full max-w-[900px] px-4 py-6 md:px-6">
        <EmptyState
          icon={ShieldCheck}
          title="Application not found"
          description="This KYC application could not be found."
          actionLabel="Back to KYC"
          onAction={() => navigate("/admin/kyc")}
        />
      </div>
    );
  }

  const reviewable = record.status === "SUBMITTED" || record.status === "UNDER_REVIEW";
  const allChecked = checklist.every(Boolean);
  const verifiedCount = checklist.filter(Boolean).length;
  const cancelledChequeMissing = !matchDoc(record, "Cancelled Cheque or Bank Statement");
  const submittedLine = record.submittedAt ? `Submitted on ${formatDate(record.submittedAt)}` : "Not submitted yet";

  function maskAadhaar(num: string): string {
    const groups = num.trim().split(/\s+/);
    const last = groups[groups.length - 1] ?? "";
    return `XXXX XXXX ${last}`;
  }

  function toggleAadhaar() {
    const next = !aadhaarRevealed;
    setAadhaarRevealed(next);
    if (next) toast.info("Full Aadhaar number viewed — this action is logged");
  }

  function handleDownload(doc: KycDocument) {
    toast.info(`Downloading ${doc.fileName}`);
  }

  async function handleApprove(remark: string) {
    if (!record) return;
    try {
      const kycId = record.id || record.applicationId;
      const res = await kycService.approve(kycId, remark);
      if (res?.data?.record) {
        setRecord(res.data.record);
      } else {
        setRecord((prev) =>
          prev ? { ...prev, status: "APPROVED", reviewedAt: new Date().toISOString(), reviewedBy: "Admin" } : null
        );
      }
      setApproveOpen(false);
      toast.success("KYC approved successfully");
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to approve KYC");
    }
  }

  async function handleReject(reason: string, remarks: string) {
    if (!record) return;
    try {
      const kycId = record.id || record.applicationId;
      const res = await kycService.reject(kycId, reason, remarks);
      if (res?.data?.record) {
        setRecord(res.data.record);
      } else {
        setRecord((prev) =>
          prev
            ? {
                ...prev,
                status: "REJECTED",
                rejectionReason: reason,
                remarks,
                reviewedAt: new Date().toISOString(),
                reviewedBy: "Admin",
              }
            : null
        );
      }
      setRejectOpen(false);
      toast.success("KYC rejected");
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to reject KYC");
    }
  }

  async function handleRefund(rid: string, amount: number, reason: string, remark: string) {
    if (!record) return;
    try {
      const kycId = record.id || record.applicationId;
      const res = await kycService.refund(kycId);
      setWallets((prev) =>
        prev.map((w) =>
          w.retailerId === rid
            ? { ...w, balance: res?.data?.newBalance ?? w.balance + amount, totalBalance: res?.data?.newBalance ?? w.totalBalance + amount }
            : w
        )
      );
      if (res?.data?.record) setRecord(res.data.record);
      setRefunded(true);
      setRefundedAt(new Date().toISOString());
      setAddOpen(false);
      toast.success("KYC fee refunded to retailer wallet");
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to refund KYC fee");
    }
  }

  const businessRows: InfoRow[] = [
    { label: "Legal Business Name", value: record.legalBusinessName || "—" },
    { label: "Trade Name", value: record.tradeName || "—" },
    { label: "Business Type", value: record.businessType ?? "—" },
    { label: "Business PAN", value: <span className="font-mono">{record.businessPan || "—"}</span> },
    { label: "GST Number", value: <span className="font-mono">{record.gstNumber || "—"}</span> },
    { label: "Year of Incorporation", value: record.yearOfIncorporation || "—" },
    { label: "Nature of Business", value: record.natureOfBusiness || "—" },
    { label: "Registered Address", value: record.registeredAddress || "—" },
    { label: "City", value: record.city || "—" },
    { label: "State", value: record.state || "—" },
    { label: "Pincode", value: record.pincode || "—" },
    { label: "Business Mobile", value: record.businessMobile ? `+91 ${record.businessMobile}` : "—" },
    { label: "Business Email", value: record.businessEmail || "—" },
  ];

  const personalRows: InfoRow[] = [
    { label: "Owner Full Name", value: record.ownerFullName || "—" },
    { label: "Date of Birth", value: record.dateOfBirth || "—" },
    { label: "Personal PAN", value: <span className="font-mono">{record.personalPan || "—"}</span> },
    {
      label: "Aadhaar Number",
      value: (
        <span className="inline-flex items-center gap-2">
          <span className="font-mono">
            {aadhaarRevealed ? record.aadhaarNumber : maskAadhaar(record.aadhaarNumber)}
          </span>
          <button
            type="button"
            onClick={toggleAadhaar}
            className="flex h-7 w-7 items-center justify-center rounded-md text-ink-muted hover:bg-slate-100 hover:text-ink cursor-pointer"
            aria-label={aadhaarRevealed ? "Hide Aadhaar number" : "Reveal Aadhaar number"}
          >
            {aadhaarRevealed ? <X className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </span>
      ),
    },
  ];

  const bankRows: InfoRow[] = [
    { label: "Account Holder Name", value: record.accountHolderName || "—" },
    { label: "Account Number", value: <span className="font-mono">{record.accountNumber || "—"}</span> },
    { label: "IFSC Code", value: <span className="font-mono">{record.ifsc || "—"}</span> },
    { label: "Bank Name", value: record.bankName || "—" },
    { label: "Branch", value: resolveBranch(record.bankName) },
    { label: "Account Type", value: record.accountType },
  ];

  const walletRows: InfoRow[] = [
    { label: "Wallet Balance", value: formatINR(available) },
    {
      label: "KYC Fee Status",
      value: record.submittedAt ? `Deducted on ${formatDate(record.submittedAt)}` : "Not yet deducted",
    },
    { label: "Retailer Since", value: (retailer as any).joinedAt },
    { label: "Account Status", value: (retailer as any).isActive ? "Active" : "Inactive" },
  ];

  const uploadedCount = requiredUploadedCount(record);
  const requiredCount = requiredDocCount(record.businessType);

  return (
    <div className="mx-auto w-full max-w-[1400px] px-4 py-6 md:px-6">
      <button
        type="button"
        onClick={() => navigate("/admin/kyc")}
        className="inline-flex w-fit items-center gap-1.5 text-sm font-medium text-ink-secondary hover:text-ink cursor-pointer"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to KYC
      </button>

      <div className="mt-4 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex items-start gap-4">
          <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#EFF6FF] text-base font-semibold text-[#1E40AF]">
            {initials(record.legalBusinessName || retailer.shopName)}
          </span>
          <div>
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="text-2xl font-semibold text-ink">{record.legalBusinessName || retailer.shopName}</h1>
              <KycStatusBadge status={record.status} />
            </div>
            <p className="mt-1 text-[13px] text-ink-secondary">
              {retailer.shopName} · {retailer.name} · {record.retailerId} · {submittedLine}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2.5">
          {reviewable ? (
            canApprove("kyc") ? (
              <>
                <button
                  type="button"
                  onClick={() => setRejectOpen(true)}
                  className="inline-flex h-10 items-center gap-2 whitespace-nowrap rounded-md border border-red-300 bg-white px-4 text-sm font-medium text-[#DC2626] hover:bg-[#FEF2F2] cursor-pointer"
                >
                  <X className="h-4 w-4" />
                  Reject
                </button>
                <button
                  type="button"
                  onClick={() => setApproveOpen(true)}
                  disabled={!allChecked}
                  title={allChecked ? undefined : "Complete the verification checklist first"}
                  className="inline-flex h-10 items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 text-sm font-medium text-white hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-50 cursor-pointer"
                >
                  <Check className="h-4 w-4" />
                  Approve
                </button>
              </>
            ) : (
              <span className="inline-flex items-center gap-1.5 rounded-md bg-amber-50 px-3 py-1.5 text-xs font-medium text-amber-800 border border-amber-200">
                <ShieldCheck className="h-4 w-4 text-amber-600" />
                Read-Only (KYC Approval Restricted)
              </span>
            )
          ) : (
            <>
              <span className="text-[13px] text-ink-secondary">
                Reviewed by {record.reviewedBy ?? "Admin"} on {record.reviewedAt ? formatDate(record.reviewedAt) : "—"}
              </span>
              <Button variant="secondary" onClick={() => toast.info("Preparing KYC PDF...")} leftIcon={<FileDown className="h-4 w-4" />}>
                Export as PDF
              </Button>
            </>
          )}
        </div>
      </div>

      {(record.status === "APPROVED" || record.status === "REJECTED") && (
        <div className="mt-5 flex flex-col gap-4 rounded-[10px] border p-4 sm:flex-row sm:items-center sm:justify-between"
          style={record.status === "APPROVED"
            ? { borderColor: "#A7F3D0", backgroundColor: "#ECFDF5" }
            : { borderColor: "#FECACA", backgroundColor: "#FEF2F2" }}>
          <div className="flex items-start gap-3">
            <span
              className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center ${
                record.status === "APPROVED" ? "text-[#059669]" : "text-[#DC2626]"
              }`}
            >
              {record.status === "APPROVED" ? (
                <CheckCircle2 className="h-5 w-5" />
              ) : (
                <XCircle className="h-5 w-5" />
              )}
            </span>
            <div>
              <p className="text-sm font-semibold text-ink">
                {record.status === "APPROVED" ? "KYC approved" : "KYC rejected"}
              </p>
              <p className="mt-0.5 text-[13px] text-ink-secondary">
                {record.status === "APPROVED"
                  ? "This retailer can create mandates."
                  : record.rejectionReason ?? "Application rejected."}
              </p>
            </div>
          </div>
          {record.status === "REJECTED" &&
            (refunded ? (
              <span className="text-xs text-ink-secondary">Fee refunded on {formatDate(refundedAt)}</span>
            ) : (
              <Button variant="secondary" onClick={() => setAddOpen(true)}>
                Refund {formatINR(KYC_FEE)}
              </Button>
            ))}
        </div>
      )}

      {loading ? (
        <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-[58%_42%]">
          <div className="skeleton-shimmer h-[640px] rounded-[10px]" />
          <div className="skeleton-shimmer h-[500px] rounded-[10px]" />
        </div>
      ) : (
        <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-[58%_42%]">
          <div className="flex flex-col gap-4">
            <InfoCard title="Business Details" rows={businessRows} />
            <InfoCard title="Personal Details" rows={personalRows} />
            <InfoCard
              title="Bank Details"
              rows={bankRows}
              footer={
                cancelledChequeMissing && (
                  <div className="mt-3 flex items-start gap-2.5 rounded-lg border border-[#FDE68A] bg-[#FFFBEB] px-3 py-2.5">
                    <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center text-[#D97706]">
                      <AlertTriangle className="h-4 w-4" />
                    </span>
                    <p className="text-xs text-ink-secondary">
                      Cancelled cheque not uploaded — verify the account details carefully before approving.
                    </p>
                  </div>
                )
              }
            />
            <InfoCard title="Wallet & Account" rows={walletRows} />
          </div>

          <div className="flex flex-col gap-4">
            <DocumentsCard
              record={record}
              onPreview={(doc) => setViewerDoc(doc)}
              onDownload={handleDownload}
            />
            {reviewable && (
              <VerificationChecklist checked={checklist} onToggle={(i) => setChecklist((c) => c.map((v, j) => (j === i ? !v : v)))} />
            )}
            <ReviewHistory record={record} />
          </div>
        </div>
      )}

      <DocumentViewerModal
        documents={record.documents}
        initialDoc={viewerDoc}
        open={!!viewerDoc}
        onClose={() => setViewerDoc(null)}
        onDownload={handleDownload}
      />
      <ApproveModal
        open={approveOpen}
        onClose={() => setApproveOpen(false)}
        record={record}
        onConfirm={handleApprove}
      />
      <RejectModal
        open={rejectOpen}
        onClose={() => setRejectOpen(false)}
        record={record}
        onConfirm={handleReject}
      />
      <AddBalanceModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        wallets={wallets}
        targetRetailerId={record.retailerId}
        onAdd={handleRefund}
        prefillAmount="2000"
        prefillReason="Refund Adjustment"
      />
    </div>
  );
}